import { RawDraftContentBlock, RawDraftContentState } from 'draft-js';
import { Element, Node, Text } from 'slate';
import { v4 } from 'uuid';
import { EditorState } from '../Editor.types';
import { blockLinkType } from '../plugins/blockLink/blockLinkType';
import { fileBlockType } from '../plugins/file/fileBlockType';
import { assignmentType } from '../plugins/assignment/assignmentType';
import { inlineLinkType } from '../plugins/inlineLink/inlineLinkType';
import {
  UnsupportedElement,
  unsupportedType,
} from '../plugins/unsupported/UnsupportedElement';
import { AulaText, RichTextMark } from '../types';
import { panoptoType } from '../plugins/panopto/panoptoType';
import { AulaEntity } from '../Entities';
import { bigBlueButtonType } from '../plugins/bigBlueButton/bigBlueButtonType';
import { blockQuoteType } from '../plugins/blockQuote/blockQuoteType';
import { codeType } from '../plugins/code/codeType';
import { planetEStreamType } from '../plugins/planetEStream/planetEStreamType';
import { echo360Type } from '../plugins/echo360/echo360Type';
import { talisType } from '../plugins/talis/talisType';
import { BibliUEntity, bibliUType } from '../plugins/bibliU/bibliUType';
import { latexType } from '../plugins/latex/latexType';
import { oneDriveType } from '../plugins/oneDrive/oneDriveType';
import { googleDriveType } from '../plugins/googleDrive/googleDriveType';
import { isSurrogatePair } from './isSurrogatePair';

const inlineStyleMap = {
  italic: 'i',
  bold: 'b',
  code: 'e',
  strike: 's',
  strikethrough: 's',
  underline: 'u',
};

const plainTextTypes = [
  'unordered-list-item',
  'unstyled',
  'ordered-list-item',
  'atomic',
];

const blockLinkTypes = [
  'gspreadsheet',
  'link',
  'video',
  'gdoc',
  'cloudapp',
  'slideshare',
  'twitter',
  'padlet',
  'flatio',
  'nearpod',
  'gslides',
  'codepen',
  'msform',
  'gform',
  'typeform',
  'github',
  'pinterest',
];

const shallowDiff = (a: any, b: any) => {
  if (a === b) {
    return false;
  }

  if (typeof a !== 'object' || typeof b !== 'object') {
    return true;
  }

  // Gets combined unique keys
  const keys = Object.keys(b)
    .concat(Object.keys(a))
    .filter((v, i, self) => self.indexOf(v) === i);

  for (const k of keys) {
    if (a[k] !== b[k]) {
      return true;
    }
  }

  return false;
};

const flattenTextNodes = (acc: AulaText[], next: AulaText) => {
  if (acc.length === 0) {
    return [next];
  }

  const { text: textPrev, ...restPrev } = acc[acc.length - 1];
  const { text: textNext, ...restNext } = next;

  if (shallowDiff(restPrev, restNext)) {
    return [...acc, next];
  }

  return [
    ...acc.slice(0, -1),
    {
      ...next,
      text: textPrev + textNext,
    },
  ];
};

const mapInlineStyles = (
  text: string,
  i: number,
  block: RawDraftContentBlock,
) => {
  const char: AulaText = {
    text,
  };

  for (const inlineStyle of block.inlineStyleRanges) {
    const start = inlineStyle.offset;
    const end = inlineStyle.offset + inlineStyle.length;
    if (start <= i && end > i) {
      const style =
        inlineStyle.style.toLowerCase() as keyof typeof inlineStyleMap;
      const styleKey = inlineStyleMap[style] as RichTextMark;
      if (styleKey != null) {
        char[styleKey] = true as any;
      }
    }
  }

  return char;
};

const isTextBlock = (val: Node): val is Element =>
  Element.isElement(val) && val.type == null;

const isEmpty = (val: Element) => {
  if (val.children.length === 0) return true;
  return (
    val.children.map((v) => Text.isText(v) && v.text).join('').length === 0
  );
};

/**
 * This method collapses adjacent text blocks into one, and inserts line breaks (\n) in between
 *
 * Background: When converting text blocks from old to new editor, we need to take into account that
 * text blocks are displayed as paragraphs by default and therefore have bigger padding around them.
 *
 * In order to maintain the same whitespace characteristics as content in the old editor format,
 * neighbouring text blocks have to be joined with linebreaks. For more info please read the description
 * of the pr https://github.com/AulaEducation/aula/pull/13511#issue-696044944
 */
const collapseAdjacentTextBlocks = ({ value, context }: EditorState) => {
  const newValue: Node[] = [];
  value.forEach((val) => {
    const prev = newValue[newValue.length - 1];
    if (
      newValue.length > 0 &&
      isTextBlock(val) &&
      isTextBlock(prev) &&
      (!isEmpty(val) || isEmpty(prev))
    ) {
      const children: Node[] = [];
      if (!isEmpty(prev)) {
        children.push({
          text: '\n',
        });
      }
      prev.children = prev.children.concat([...children, ...val.children]);
    } else {
      newValue.push(val);
    }
  });

  return {
    value: newValue,
    context,
  };
};

export const convertLegacyContent = (
  content: RawDraftContentState,
): EditorState => {
  const editorState: EditorState = {
    value: [],
    context: {
      entities: {},
    },
  };

  for (const block of content.blocks) {
    // currently only unstyled blocks are transformed into rich text,
    // all other blocks will be converted to the unsupported block type
    if (plainTextTypes.includes(block.type) && block.data?.type == null) {
      const chars: AulaText[] = block.text
        .split('')
        .reduce((acc, current) => {
          if (acc.length > 0) {
            const prev = acc[acc.length - 1];
            /**
             * if the current and previous character is a surrogate pair (such as an emoji)
             * we join them together.
             */
            if (isSurrogatePair(prev, current)) {
              return acc.slice(0, -1).concat([`${prev}${current}`]);
            }
          }

          return [...acc, current];
        }, [] as string[])
        .map((text, i) => mapInlineStyles(text, i, block));

      let children: Node[] = [];

      if (block.entityRanges == null || block.entityRanges.length === 0) {
        children = chars.reduce(flattenTextNodes, []);
      } else {
        let lastPosition = 0;

        const sortedEntityRanges = block.entityRanges.sort(
          (a, b) => a.offset - b.offset,
        );

        sortedEntityRanges.forEach((entityRange, i) => {
          const start = entityRange.offset;
          const end = entityRange.offset + entityRange.length;
          children = children.concat(
            chars.slice(lastPosition, start).reduce(flattenTextNodes, []),
          );
          const textNodes = chars.slice(start, end);
          const entity = content.entityMap[entityRange.key];
          const element: Element = {
            children: textNodes.reduce(flattenTextNodes, []),
          };

          if (entity?.type === 'LINK') {
            element.type = inlineLinkType;
            if (entity?.data?.href != null) {
              element.url = entity?.data?.href;
            }
          }

          children.push(element);
          lastPosition = end;

          const sliceTo =
            sortedEntityRanges[i + 1] != null ? lastPosition : chars.length;

          children = children.concat(
            chars.slice(lastPosition, sliceTo).reduce(flattenTextNodes, []),
          );
        });
      }

      if (children.length === 0) {
        children.push({
          text: '',
        });
      }

      editorState.value.push({
        children,
      });
    } else if (
      block.type === 'atomic' &&
      block.data?.type === 'file' &&
      block.data?.source === 'link'
    ) {
      // File blocks with a `link` source get converted to regular link blocks:
      const entity = v4();

      editorState.context.entities[entity] = {
        type: 'meta',
        url: block.data.url || block.data.file.fileUrl,
        data: {
          // Preserves the `file` object within the data:
          ...block.data,
          title: block.data.file.name,
        },
      };

      editorState.value.push({
        type: blockLinkType,
        entity,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (
      block.type === 'atomic' &&
      block.data?.type === 'file' &&
      block.data?.source === 'onedrive'
    ) {
      editorState.value.push({
        type: oneDriveType,
        title: block.data.file.name,
        viewUrl: block.data.file?.fileUrl,
        // The TH90 OneDrive plugin does not store embeddable urls in the file object, but
        // a regular 'view' url (same as data.file.fileUrl, data.url), which will not work
        // when embedded.
        embedUrl: undefined,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (
      block.type === 'atomic' &&
      block.data?.type === 'file' &&
      block.data?.source === 'googledrive'
    ) {
      editorState.value.push({
        type: googleDriveType,
        title: block.data.file.name,
        viewUrl: block.data.file?.fileUrl,
        embedUrl: block.data.file?.embedUrl,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (
      block.type === 'atomic' &&
      block.data?.type === 'file' &&
      // Do not try to convert Dropbox files, risking data loss, until a conversion exists:
      block.data?.source !== 'dropbox'
    ) {
      const entity = block.data.file.fileItemId || v4();

      editorState.context.entities[entity] = {
        type: 'file',
        file: block.data.file,
        data: block.data.file,
      };

      editorState.value.push({
        type: fileBlockType,
        entity,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'spacing') {
      // Do nothing, we won't want spacing blocks in the Aula Editor state
    } else if (
      (block.type === 'atomic' && block.data?.type === 'quote') ||
      (block.type === 'unstyled' && block.data?.type === 'quote') ||
      block.type === 'blockquote'
    ) {
      editorState.value.push({
        type: blockQuoteType,
        children: [
          {
            text: block.text,
          },
        ],
      });
    } else if (
      block.type === 'atomic' &&
      block.data != null &&
      blockLinkTypes.includes(block.data?.type || '') &&
      (block.data.link || block.data.url)
    ) {
      const url = block.data.link || block.data.url;
      const entity = v4();

      editorState.context.entities[entity] = {
        type: 'meta',
        url,
        ...(block.data.expanded === true ? { expanded: true } : {}),
        data: {
          title: block.data.title,
          ...block.data,
        },
      };

      editorState.value.push({
        type: blockLinkType,
        entity,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (
      block.type === 'atomic' &&
      block.data != null &&
      blockLinkTypes.includes(block.data?.type || '') &&
      // Guard clause, even though the above branch should be the inverse of this:
      !(block.data?.link || block.data?.url)
    ) {
      // Edge case: legacy TH90 link type with an empty-string link or url gets converted to plain text:
      editorState.value.push({
        children: [{ text: block.text }],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'assignments') {
      const entity = v4();

      editorState.context.entities[entity] = {
        type: assignmentType,
        assignmentId: block.data.assignment.id,
        data: { assignmentId: block.data.assignment.id },
      };

      editorState.value.push({
        type: assignmentType,
        entity,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'panopto') {
      const { contentId, linkId } = block.data;
      const entity = v4();

      editorState.context.entities[entity] = {
        type: 'lti',
        linkId,
        params: { launchType: 'basic', contentId },
        data: { contentId, linkId },
      } as AulaEntity;

      editorState.value.push({
        type: panoptoType,
        entity,
        linkId,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'bibliu') {
      const { url } = block.data;
      const entity = v4();
      const linkId = v4();

      editorState.context.entities[entity] = {
        type: 'lti',
        data: { linkId },
        url,
      } as BibliUEntity;

      editorState.value.push({
        type: bibliUType,
        entity,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'code-block') {
      editorState.value.push({
        type: codeType,
        language:
          block.data?.syntax?.length > 0 ? block.data?.syntax : undefined,
        children: block.text.split('\n').map((v) => ({
          children: [
            {
              text: v,
            },
          ],
        })),
      });
    } else if (block.type === 'atomic' && block.data?.type === 'bbb') {
      const linkId = v4();
      const entity = v4();

      editorState.context.entities[entity] = {
        type: 'lti',
        linkId,
        params: { linkId },
        data: { linkId },
      } as AulaEntity;

      editorState.value.push({
        type: bigBlueButtonType,
        entity,
        linkId,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'planet') {
      const { linkId } = block.data;
      const entity = v4();

      editorState.context.entities[entity] = {
        type: 'lti',
        linkId,
        params: { linkId },
        data: { linkId },
      } as AulaEntity;

      editorState.value.push({
        type: planetEStreamType,
        entity,
        linkId,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'echo') {
      const { linkId } = block.data;
      const entity = v4();

      editorState.context.entities[entity] = {
        type: 'lti',
        linkId,
        params: { linkId },
        data: { linkId },
      } as AulaEntity;

      editorState.value.push({
        type: echo360Type,
        entity,
        linkId,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'talis') {
      const { linkId } = block.data;
      const entity = v4();

      editorState.context.entities[entity] = {
        type: 'lti',
        linkId,
        params: { linkId },
        data: { linkId },
      } as AulaEntity;

      editorState.value.push({
        type: talisType,
        entity,
        linkId,
        children: [
          {
            text: '',
          },
        ],
      });
    } else if (block.type === 'atomic' && block.data?.type === 'latex') {
      editorState.value.push({
        type: latexType,
        children: [
          {
            text: block.text,
          },
        ],
      });
    } else {
      const unsupportedBlock: UnsupportedElement = {
        type: unsupportedType,
        legacyContent: block,
        children: [
          {
            text: '',
          },
        ],
      };
      editorState.value.push(unsupportedBlock);
    }
  }

  return collapseAdjacentTextBlocks(editorState);
};
